import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Spacer,
  Chips,
  Divider,
  Typography,
  Button,
  Stack,
} from "digiwash-components";

import Header from "../components/AppHeader";
import PageContainer from "../components/PageContainer";

import OrdersCard from "../containers/OrdersCard";

import { Platform, ScrollView, TouchableOpacity } from "react-native";
import { Helmet } from "react-helmet";
import useOrdersList from "../ducks/user/hooks/useOrdersList";
import useSyncFilterWithParam from "../hooks/useSyncFilterWithParam";
import HeaderContainer from "../containers/HeaderContainer";
import { useListenToDeepLinks } from "../hooks/useListenToDeepLinks";
import CustomIcon from "../components/CustomIcon";

import { boxRatioWidth } from "../helpers/general";
import BoxRatio from "../components/BoxRatio";
import useNavigation from "../hooks/useNavigation";
import dayjs from "dayjs";

const webStyles =
  Platform.OS === "web"
    ? {
        scrollViewContainer: {
          width: "100%",
        },
      }
    : {};

let orderFilterItems = [
  {
    title: "همه",
    status: "all",
  },
  {
    title: "در حال انجام",
    status: "in_progress",
  },
  {
    title: "تحویل شده",
    status: "delivered",
  },
  {
    title: "لغو شده",
    status: "canceled",
  },
];

function sortFilteredDataByDate(data) {
  let dataWithCollection = data.filter((i) => !!i.collection);
  let dataWithoutCollection = data.filter((i) => !i.collection);
  dataWithCollection.sort((a, b) => {
    let a_date = a.collection?.period.date;
    let b_date = b.collection?.period.date;
    if (!!a.collection && !!b.collection) {
      if (a_date === b_date) {
        return 0;
      }
      return dayjs(a_date).isBefore(dayjs(b_date)) ? 1 : -1;
    }
  });
  dataWithoutCollection.forEach((i) => {
    dataWithCollection.push(i);
  });

  return dataWithCollection;
}

function UserOrdersList() {
  let nav = useNavigation();
  let [filter, setFilter] = useState<string>("all");
  let { setParam, params } = useSyncFilterWithParam();
  let status = params?.status;
  useListenToDeepLinks();

  let containerRef = useRef(null);

  let { data, isValidating, error, revalidate } = useOrdersList();
  let filteredData = [];

  if (data) {
    if (filter === "all") {
      filteredData = data.filter((order) => {
        return !["submitted_by_customer", "submitted_by_crm"].includes(
          order.latest_status
        );
      });
    } else if (filter === "in_progress") {
      filteredData = data.filter((order) => {
        return [
          "ready_to_collect",
          "collected",
          "collection_not_present",
          "unwrapped",
          "in_progress",
          "work_done",
          "ready_to_deliver",
          "delivery_not_present",
          "delivery_canceled",
          "collection_canceled",
        ].includes(order.latest_status);
      });
    } else if (filter === "delivered") {
      filteredData = data.filter((order) => {
        return ["delivered"].includes(order.latest_status);
      });
    } else if (filter === "canceled") {
      filteredData = data.filter((order) => {
        return ["canceled_by_customer", "canceled_by_system"].includes(
          order.latest_status
        );
      });
    }
  }

  let isEmptyList = !isValidating && filteredData.length === 0;
  let emptyText = undefined;
  if (isEmptyList && filter !== "all" && !error) {
    emptyText = "سفارشی یافت نشد.";
  }

  let listIsEmpty =
    !emptyText &&
    !isValidating &&
    filteredData &&
    filteredData.length === 0 &&
    !error;

  useEffect(() => {
    if (status) {
      setFilter(status);
    }
  }, [status]);

  useEffect(() => {
    revalidate();
  }, []);

  return (
    <>
      <Helmet>
        <title>سفارش‌ها | دیجی واش</title>
      </Helmet>
      <PageContainer
        showBottomNavigation
        extraSpaceFromBottom={true}
        bg="common.white"
        insideBeignScrollView={
          <HeaderContainer>
            <Header title="لیست سفارش‌ها" />
          </HeaderContainer>
        }
      >
        <Box pt="xxl">
          {listIsEmpty ? (
            <>
              <Divider size="xxxl" />
              <BoxRatio
                fixedWidth={boxRatioWidth({
                  columns: 1,
                  padding: ["xxl", "xxl"],
                })}
                ratio="2:1"
              >
                <CustomIcon icon="ordersEmpty" width="100%" height="100%" />
              </BoxRatio>
              <Divider size="xxl" />
              <Typography color="gray.light950" fs="l" alignment="center">
                شما هنوز سفارشی ثبت نکرده اید
              </Typography>
              <Divider size="xxl" />
              <Stack distribution="center">
                <Box style={{ width: "100%", maxWidth: 300 }}>
                  <Button
                    onPress={() => {
                      nav.navigate("Services", { id: "cats_1" });
                    }}
                  >
                    ثبت سفارش جدید
                  </Button>
                </Box>
              </Stack>
            </>
          ) : (
            <>
              <Box style={{ alignItems: "flex-end" }}>
                <ScrollView
                  onContentSizeChange={() =>
                    Platform.OS === "android"
                      ? containerRef.current?.scrollToEnd({ animated: false })
                      : null
                  }
                  horizontal
                  contentContainerStyle={{
                    flexGrow: 1,
                  }}
                  showsHorizontalScrollIndicator={false}
                  ref={containerRef}
                  key="Scrolling"
                  style={{
                    ...{
                      flexGrow: 1,
                      direction: "rtl",
                    },
                    ...webStyles.scrollViewContainer,
                  }}
                >
                  <Box
                    px="l"
                    style={{
                      flexGrow: 1,
                      flexDirection:
                        Platform.OS === "web" ? "row" : "row-reverse",
                    }}
                  >
                    {orderFilterItems.map((item) => {
                      return (
                        <>
                          <TouchableOpacity
                            onPress={() => {
                              setFilter(item.status);
                              if (Platform.OS === "web") {
                                setParam({
                                  name: "status",
                                  value: item.status,
                                });
                              }
                            }}
                          >
                            <Chips
                              title={item.title}
                              type={
                                filter === item.status ? "primary" : "light"
                              }
                              size="normal"
                            />
                          </TouchableOpacity>
                          <Spacer size="m" />
                        </>
                      );
                    })}
                  </Box>
                </ScrollView>
              </Box>
              <Box p="l">
                <OrdersCard
                  isValidating={isValidating}
                  data={sortFilteredDataByDate(filteredData)}
                  error={error}
                  emptyText={emptyText}
                />
              </Box>
            </>
          )}
        </Box>
      </PageContainer>
    </>
  );
}

export default UserOrdersList;
